import React, { useEffect, useRef, useState } from 'react';
import 'react-dates/initialize';
import { CUSTOM_ARIA_LABELS, getRem, SHORTCUT_CHIPS_POSITIONS, useCustomPhrases } from 'lib/core';
import { DATE_FORMATS } from 'lib/date-input';
import { IconToday } from 'lib/icons';
import { isFocused, KEY_VALUES, useKeyDownEventListener, useShareForwardedRef, useWindowSize } from 'lib/utilities';
import moment from 'moment';
import PropTypes from 'prop-types';
import { DayPickerSingleDateController } from 'react-dates';
import { DayPickerPhrases } from 'react-dates/lib/defaultPhrases';
import momentPropTypes from 'react-moment-proptypes';
import styled, { css } from 'styled-components';
import { isMobileScreen } from '../../../core';
import { ChipDatePickerContainer, ChipShortcutContainer } from '../../elements';
import {
  getToday,
  getTomorrow,
  getYesterday,
  isAnyDateOutsideRange,
  isCalendarDayFocused,
  isDateBlocked,
  isDateInPast,
  isDateToday,
} from '../../utils';
import { Chip } from '../Chip';

const StyledChipShortcutChip = styled(Chip)`
  margin-top: ${({ theme }) => theme.size.spacing.medium.value};
  ${({ isBottom }) =>
    isBottom &&
    css`
      max-width: ${getRem('272px')};
      &:not(:last-child) {
        margin-right: ${({ theme }) => theme.size.spacing.medium.value};
      }
    `}
  ${({ isRight }) =>
    isRight &&
    css`
      display: flex;
      width: 100%;
    `}
`;

const ChipShortcutChip = React.forwardRef((props, ref) => <StyledChipShortcutChip {...props} ref={ref} />);

const DatePickerChips = React.forwardRef(
  (
    {
      allowDaysInPast = false,
      ariaLabels = {
        jumpToNextMonth: CUSTOM_ARIA_LABELS.NEXT_MONTH,
        jumpToPrevMonth: CUSTOM_ARIA_LABELS.PREVIOUS_MONTH,
      },
      blockedDateRanges = [],
      className = '',
      dataTestId = undefined,
      date = undefined,
      dateFormat = DATE_FORMATS.MM_DD_YYYY,
      dateInputRef = undefined,
      defaultIsOpen = false,
      highlightToday = false,
      id,
      label = 'Select date',
      numberOfMonths = 2,
      onClose = () => {},
      onDateChange,
      onOpen = () => {},
      onRemove = () => {},
      shortcutChipsPosition = SHORTCUT_CHIPS_POSITIONS.NONE,
      shortcutLabels = {
        today: 'Today',
        tomorrow: 'Tomorrow',
        yesterday: 'Yesterday',
      },
      ...other
    },
    ref
  ) => {
    const chipInputRef = useShareForwardedRef(dateInputRef);
    const datePickerRef = useShareForwardedRef(ref);
    const lastShortcutChipRef = useRef();

    const [dateInputValue, setDateInputValue] = useState('');
    const [isCalendarOpen, setIsCalendarOpen] = useState(defaultIsOpen);
    const [isCalendarFocused, setIsCalendarFocused] = useState(false);
    const [isNavNextFocused, setIsNavNextFocused] = useState(false);
    const [isNavPrevFocused, setIsNavPrevFocused] = useState(false);

    const windowSize = useWindowSize();
    const numberOfMonthsShown = isMobileScreen(windowSize.width) ? 1 : numberOfMonths;

    const customPhrases = useCustomPhrases(ariaLabels, DayPickerPhrases);

    const shortcutChipsPositioning = isMobileScreen(windowSize.width)
      ? SHORTCUT_CHIPS_POSITIONS.BOTTOM
      : shortcutChipsPosition;

    const shouldRenderCalendarInfo = shortcutChipsPosition !== SHORTCUT_CHIPS_POSITIONS.NONE;

    const handleFocusNavNext = () => {
      setIsNavPrevFocused(false);
      setIsNavNextFocused(true);
    };

    const handleFocusNavPrev = () => {
      setIsNavPrevFocused(true);
      setIsNavNextFocused(false);
    };

    const openCalendar = () => {
      setIsCalendarOpen(true);
      setTimeout(() => {
        setIsCalendarFocused(true);
      }, 0);
      onOpen();
    };

    const closeCalendar = () => {
      setIsCalendarOpen(false);
      setIsCalendarFocused(false);
      onClose();
    };

    const moveFocusToChip = () => {
      setTimeout(() => {
        return chipInputRef.current.focus();
      }, 0);
    };

    const preventScroll = (event) => {
      event.preventDefault();
    };

    useKeyDownEventListener(
      KEY_VALUES.ESCAPE,
      () => {
        closeCalendar();
        moveFocusToChip();
      },
      datePickerRef.current
    );

    useKeyDownEventListener(
      KEY_VALUES.ARROW_DOWN,
      (event) => {
        preventScroll(event);

        if (isCalendarOpen) {
          setIsCalendarFocused(true);
        } else {
          openCalendar();
        }
      },
      datePickerRef.current
    );

    useKeyDownEventListener(
      KEY_VALUES.SPACE,
      (event) => {
        const isNavigationIconFocused = isNavNextFocused || isNavPrevFocused;

        if (isNavigationIconFocused) {
          preventScroll(event);
          setIsNavPrevFocused(false);
          setIsNavNextFocused(false);
        }

        if (!isCalendarOpen) {
          openCalendar();
        }
      },
      datePickerRef.current
    );

    useKeyDownEventListener(
      KEY_VALUES.TAB,
      (event) => {
        const shortcutChipsAreShown = shortcutChipsPosition !== SHORTCUT_CHIPS_POSITIONS.NONE;

        const isShiftKeyPressed = event.shiftKey;
        const isDateInputFocused = isFocused(chipInputRef.current);
        const isDayFocused = isCalendarDayFocused(datePickerRef);
        const isLastShortcutChipFocused = shortcutChipsAreShown && isFocused(lastShortcutChipRef.current);

        if (isDateInputFocused) {
          closeCalendar();
          return;
        }

        if (isNavPrevFocused) {
          handleFocusNavNext();
          return;
        }

        if (isNavNextFocused) {
          if (isShiftKeyPressed) {
            handleFocusNavPrev();
            return;
          }

          setIsNavNextFocused(false);
          return;
        }

        if (isDayFocused) {
          if (isShiftKeyPressed) {
            handleFocusNavNext();
            return;
          }

          if (shortcutChipsAreShown) {
            return;
          }

          moveFocusToChip();
          closeCalendar();
          return;
        }

        if (isLastShortcutChipFocused) {
          if (isShiftKeyPressed) {
            return;
          }

          moveFocusToChip();
          closeCalendar();
        }
      },
      datePickerRef.current
    );

    useEffect(() => {
      const updateDateInputValueOnDateChange = () => {
        if (date) {
          const value = date.format(dateFormat);
          setDateInputValue(value);
        } else {
          setDateInputValue('');
        }
      };

      updateDateInputValueOnDateChange();
    }, [date, dateFormat]);

    const handleCalendarDayClick = (momentValue) => {
      onDateChange(momentValue);
      moveFocusToChip();
      closeCalendar();
    };

    const handleOutsideClick = (event) => {
      const elementClicked = event.target;

      const clickedOnDateInput = chipInputRef.current.contains(elementClicked);

      if (!clickedOnDateInput) {
        closeCalendar();
      }
    };

    const isBottom = isMobileScreen(windowSize.width) || shortcutChipsPosition === SHORTCUT_CHIPS_POSITIONS.BOTTOM;
    const isRight = !isMobileScreen(windowSize.width) && shortcutChipsPosition === SHORTCUT_CHIPS_POSITIONS.RIGHT;

    const renderShortcuts = () => {
      const shortcutChipsProps = [
        { label: shortcutLabels.today, onClick: () => handleCalendarDayClick(getToday()) },
        { label: shortcutLabels.tomorrow, onClick: () => handleCalendarDayClick(getTomorrow()) },
      ];

      if (allowDaysInPast) {
        shortcutChipsProps.push({
          label: shortcutLabels.yesterday,
          onClick: () => handleCalendarDayClick(getYesterday()),
        });
      }

      return (
        <ChipShortcutContainer isBottom={isBottom} isRight={isRight}>
          {shortcutChipsProps.map((chipProps, i) => (
            <ChipShortcutChip
              data-testid={dataTestId ? `${dataTestId}-shortcut-chip-${chipProps.label}` : undefined}
              key={chipProps.label}
              isBottom={isBottom}
              isRight={isRight}
              ref={i === shortcutChipsProps.length - 1 ? lastShortcutChipRef : undefined}
              {...chipProps}
            />
          ))}
        </ChipShortcutContainer>
      );
    };

    const handleClear = (evt) => {
      // reset all internal state
      preventScroll(evt);
      evt.stopPropagation();
      setDateInputValue('');
      setIsCalendarFocused(false);
      moveFocusToChip();
      onRemove();
    };
    return (
      <ChipDatePickerContainer data-testid={dataTestId} id={id} ref={datePickerRef} className={className}>
        <Chip
          dataTestId={dataTestId}
          label={dateInputValue || label}
          onClick={isCalendarOpen ? closeCalendar : openCalendar}
          ref={chipInputRef}
          isOpen={isCalendarOpen}
          isSelected={!!dateInputValue}
          hasDropdown={!dateInputValue}
          onRemove={dateInputValue ? handleClear : undefined}
          icon={<IconToday />}
        />
        {isCalendarOpen && (
          <DayPickerSingleDateController
            calendarInfoPosition={
              shortcutChipsPosition !== SHORTCUT_CHIPS_POSITIONS.NONE ? shortcutChipsPositioning : undefined
            }
            date={date}
            daySize={40}
            focused={isCalendarOpen}
            hideKeyboardShortcutsPanel
            horizontalMonthPadding={6}
            isDayBlocked={isDateBlocked(blockedDateRanges)}
            isDayHighlighted={highlightToday ? isDateToday : undefined}
            isFocused={isCalendarFocused}
            isOutsideRange={allowDaysInPast ? isAnyDateOutsideRange : isDateInPast}
            numberOfMonths={numberOfMonthsShown}
            onDateChange={handleCalendarDayClick}
            onOutsideClick={handleOutsideClick}
            phrases={customPhrases}
            renderCalendarInfo={shouldRenderCalendarInfo ? renderShortcuts : undefined}
            onPrevMonthClick={handleFocusNavPrev}
            onNextMonthClick={handleFocusNavNext}
            {...other}
          />
        )}
      </ChipDatePickerContainer>
    );
  }
);

DatePickerChips.propTypes = {
  /** If true, it is allowed to pick days in past */
  allowDaysInPast: PropTypes.bool,
  /** Sets text that will be displayed for aria-labels required for screen readers */
  ariaLabels: PropTypes.shape({
    /** Text displayed for next month navigation arrow */
    jumpToNextMonth: PropTypes.node.isRequired,
    /** Text displayed for previous month navigation arrow */
    jumpToPrevMonth: PropTypes.node.isRequired,
  }),
  /** An array of date ranges that should be blocked for user to select. */
  blockedDateRanges: PropTypes.arrayOf(
    PropTypes.shape({
      /** Last day inclusive of blocked date range. */
      endDate: PropTypes.instanceOf(moment),
      /** First day inclusive of blocked date range. */
      startDate: PropTypes.instanceOf(moment),
    })
  ),
  /** Adds additional class for datePicker */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Value of datepicker date. It's type is a moment object. Set it to null to reset the picker */
  date: momentPropTypes.momentObj,
  /** Defines in what format date input should format value (e.g. YYYY/MM/DD, MM/DD/YYYY, DD/MM/YYYY) */
  dateFormat: PropTypes.oneOf(Object.values(DATE_FORMATS)),
  /** Props that are supplied to DateInput used in this component. */
  /** Access DOM element of date input field */
  dateInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLDivElement) }),
  ]),
  /** If true, date picker is open on initial render */
  defaultIsOpen: PropTypes.bool,
  /** If true, today on the calendar is highlighted */
  highlightToday: PropTypes.bool,
  /** Unique identifier to identify date picker component */
  id: PropTypes.string.isRequired,
  /** Label of the date chip field */
  label: PropTypes.node.isRequired,
  /** Defines how many months should be shown on calendar */
  numberOfMonths: PropTypes.number,
  /** Callback that is called when date picker should close */
  onClose: PropTypes.func,
  /** Callback that is called when clicking remove on chip */
  onRemove: PropTypes.func,
  /** Callback that is called when date picker value changes.
   * It takes one argument: new date value of type PropTypes.instanceOf(moment) */
  onDateChange: PropTypes.func.isRequired,
  /** Callback that is called when date picker should open */
  onOpen: PropTypes.func,
  /** Sets where shortcut chips should be rendered */
  shortcutChipsPosition: PropTypes.oneOf(Object.values(SHORTCUT_CHIPS_POSITIONS)),
  /** Sets text that will be displayed for today, tomorrow and yesterday shortcuts */
  shortcutLabels: PropTypes.shape({
    /** Text displayed for today shortcut */
    today: PropTypes.node.isRequired,
    /** Text displayed for tomorrow shortcut */
    tomorrow: PropTypes.node.isRequired,
    /** Text displayed for yesterday shortcut */
    yesterday: PropTypes.node.isRequired,
  }),
};

export { DatePickerChips };
